/**
 * Google 広告 API 連携設定ページ。
 *
 * 組織 admin が Google アカウントで連携し、その Google アカウントから操作できる
 * 全 Google 広告アカウント (MCC マネージャー + 配下クライアント) を自動検出して
 * テーブルに表示する。複数アカウントの個別接続も customer_id 入力で可能。
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { useUser } from '../../features/auth/useUser';
import { Button } from '../../components/common/Button';
import { CreateButton } from '../../components/common/CreateButton';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../components/common/useConfirmDialog';
import { BackButton } from '../../components/common/BackButton';
import { Label } from '../../components/common/Label';
import type { LabelVariant } from '../../components/common/Label';
import { apiClient } from '../../services/apiClient';
import { getErrorMessage } from '../../utils/getErrorMessage';
import { NewConnectionModal } from './NewConnectionModal';
import '../../components/common/CommonTable.css';
import '../../components/common/CommonFilters.css';
import '../products/ProductsPageHeader.css';
import '../amazon/AmazonAuthPage.css';

const CUSTOMER_ID_STORAGE_KEY = 'connections.google-ads.customer_id_draft';

interface GoogleAdsAccount {
  id: string;
  platform: string;
  account_id: string;
  account_display_name: string | null;
  status: string;
  is_manager: boolean;
  parent_credential_id: string | null;
  created_at: string;
  last_used_at: string | null;
  metadata: Record<string, unknown>;
}

interface PlatformCredentialsResponse {
  platform: string;
  connected: boolean;
  accounts: GoogleAdsAccount[];
}

const CALLBACK_ERROR_LABELS: Record<string, string> = {
  access_denied: '同意画面でキャンセルされました',
  missing_params: 'コールバックパラメータが不足しています',
  invalid_state: 'OAuth state が無効です。もう一度お試しください',
  state_expired: 'OAuth state の有効期限が切れました。もう一度お試しください',
  state_lookup_failed: 'OAuth state の確認に失敗しました',
  token_exchange_failed: 'Google からトークンを取得できませんでした',
  missing_refresh_token:
    'リフレッシュトークンが取得できませんでした。Google アカウント側で当アプリの権限を一度削除してから再度連携してください',
  config_error: 'サーバ設定でエラーが発生しました',
  unexpected_error: '想定外のエラーが発生しました',
};

function getStatusLabel(status: string | undefined): { text: string; variant: LabelVariant } {
  switch (status) {
    case 'active':
      return { text: '有効', variant: 'green' };
    case 'expired':
      return { text: '期限切れ', variant: 'yellow' };
    case 'revoked':
      return { text: '失効', variant: 'red' };
    default:
      return { text: status ?? '—', variant: 'gray' };
  }
}

function getAccountTypeLabel(account: GoogleAdsAccount): { text: string; variant: LabelVariant } {
  if (account.is_manager) return { text: 'MCC マネージャー', variant: 'purple' };
  if (account.parent_credential_id) return { text: 'MCC クライアント', variant: 'blue' };
  return { text: '通常アカウント', variant: 'gray' };
}

export default function GoogleAdsAuthPage() {
  const toast = useToast();
  const { confirm, dialog } = useConfirmDialog();
  const { isAdmin } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [customerIdDraft, setCustomerIdDraft] = useState(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(CUSTOMER_ID_STORAGE_KEY) ?? '';
  });
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CUSTOMER_ID_STORAGE_KEY, customerIdDraft);
  }, [customerIdDraft]);

  const callbackError = searchParams.get('ad_connect_error');
  const callbackConnected = searchParams.get('ad_connect_connected');
  // React.StrictMode により useEffect は dev モードで 2 回呼ばれるため、
  // ref で alert の重複表示を防ぐ (Amazon 広告ページと同じパターン)。
  const hasShownAlert = useRef(false);

  useEffect(() => {
    if (!callbackConnected && !callbackError) return;
    if (hasShownAlert.current) return;
    hasShownAlert.current = true;

    if (callbackConnected) {
      toast.success('Google広告アカウントの連携に成功しました');
      queryClient.invalidateQueries({ queryKey: ['connections-google-ads-credentials'] });
      navigate('/connections/google-ads', { replace: true });
    } else if (callbackError) {
      const msg = CALLBACK_ERROR_LABELS[callbackError] ?? `エラーが発生しました (${callbackError})`;
      toast.error(`Google広告アカウントの連携に失敗しました: ${msg}`);
      navigate('/connections/google-ads', { replace: true });
    }
  }, [callbackConnected, callbackError, navigate, queryClient, toast]);

  const credQuery = useQuery<PlatformCredentialsResponse>({
    queryKey: ['connections-google-ads-credentials'],
    queryFn: async () => {
      const res = await apiClient.get<PlatformCredentialsResponse>(
        '/ad-connect/credentials/google_ads',
      );
      return res.data;
    },
  });

  const startAuthMutation = useMutation({
    mutationFn: async (body: { customer_id?: string }) => {
      const res = await apiClient.post<{ url: string }>('/ad-connect/google_ads/oauth/authorize', body);
      return res.data;
    },
    onSuccess: (res) => {
      window.location.href = res.url;
    },
    onError: (err: unknown) => {
      setActionError(getErrorMessage(err, '認証の開始に失敗しました'));
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async (account_id: string) => {
      await apiClient.delete(`/ad-connect/credentials/google_ads/${account_id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['connections-google-ads-credentials'] });
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err, '連携解除に失敗しました'));
    },
  });

  const handleAutoDiscoverConnect = () => {
    setActionError(null);
    startAuthMutation.mutate({});
  };

  const handleSpecificCustomerConnect = () => {
    setActionError(null);
    const cid = customerIdDraft.replace(/[-\s]/g, '');
    if (!/^\d{8,}$/.test(cid)) {
      setActionError('customer_id は数字 (10 桁前後) で入力してください');
      return;
    }
    startAuthMutation.mutate({ customer_id: cid });
  };

  const handleDisconnect = async (account: GoogleAdsAccount) => {
    const ok = await confirm({
      title: `Google広告アカウント (customer_id: ${account.account_id}) の接続を解除しますか？`,
      message: account.is_manager ? 'MCCマネージャーのため、配下のクライアントの連携もすべて解除されます。' : undefined,
      confirmLabel: '解除する',
      variant: 'danger',
    });
    if (!ok) return;
    disconnectMutation.mutate(account.account_id);
  };

  const accounts: GoogleAdsAccount[] = credQuery.data?.accounts ?? [];

  return (
    <div className="page admin-page">
      <div className="header-actions">
        <BackButton to="/connections" />
      </div>

      <header className="page-header page-header--child">
        <div className="page-header-left">
          <h1 className="page-title">Google 広告 API 連携</h1>
          <p className="page-description">
            Google 広告と連携して、生成した素材をアセットライブラリへ直接アップロードできます。
          </p>
        </div>
      </header>

      {!isAdmin && (
        <div className="readonly-banner" role="status">
          <span className="readonly-banner-icon">
            <Eye size={18} aria-hidden="true" />
          </span>
          <span>閲覧のみ可能です。連携・解除等の操作には管理者権限が必要です。</span>
        </div>
      )}

      <section className="accounts-section">
        <div className="accounts-section-head">
          <h2 className="section-title">接続済みアカウント</h2>
          {isAdmin && (
            <CreateButton onClick={() => setIsModalOpen(true)}>新規接続</CreateButton>
          )}
        </div>

        {credQuery.isLoading && <div className="admin-loading">読み込み中...</div>}

        {credQuery.isError && (
          <div className="admin-error">広告アカウントの取得に失敗しました</div>
        )}

        {!credQuery.isLoading && !credQuery.isError && accounts.length === 0 && (
          <div className="admin-empty-state">
            <div className="admin-empty-state-title">
              接続済みの Google 広告アカウントはありません
            </div>
            <div className="admin-empty-state-description">
              「新規接続」ボタンからアカウントを接続してください。
            </div>
          </div>
        )}

        {!credQuery.isLoading && accounts.length > 0 && (
          <div className="table-container">
            <table className="common-table">
              <thead>
                <tr>
                  <th className="col-profile-id">customer_id</th>
                  <th className="col-profile-name">表示名</th>
                  <th className="col-account-type">種別</th>
                  <th className="col-status">ステータス</th>
                  <th className="col-sync-at">連携日時</th>
                  <th className="col-sync-at">最終利用</th>
                  {isAdmin && <th className="col-actions">操作</th>}
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const status = getStatusLabel(account.status);
                  const accountType = getAccountTypeLabel(account);
                  return (
                    <tr key={account.id}>
                      <td className="col-profile-id">
                        <code>{account.account_id}</code>
                      </td>
                      <td className="col-profile-name">{account.account_display_name || '—'}</td>
                      <td className="col-account-type">
                        <Label variant={accountType.variant}>{accountType.text}</Label>
                      </td>
                      <td className="col-status">
                        <Label variant={status.variant}>{status.text}</Label>
                      </td>
                      <td className="col-sync-at">
                        {account.created_at
                          ? new Date(account.created_at).toLocaleString('ja-JP')
                          : '—'}
                      </td>
                      <td className="col-sync-at">
                        {account.last_used_at
                          ? new Date(account.last_used_at).toLocaleString('ja-JP')
                          : '—'}
                      </td>
                      {isAdmin && (
                        <td className="col-actions">
                          <Button
                            onClick={() => void handleDisconnect(account)}
                            variant="danger"
                            size="sm"
                            disabled={disconnectMutation.isPending}
                          >
                            接続解除
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <NewConnectionModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setActionError(null);
        }}
        title="Google 広告アカウントに新規接続"
        description="Google アカウントで連携すると、そのアカウントから操作できる全 Google 広告アカウントを自動検出して取り込みます。MCC を持っている場合は配下のクライアントもまとめて連携されます。"
      >
        <div className="connect-form">
          <Button
            onClick={handleAutoDiscoverConnect}
            variant="primary"
            size="md"
            disabled={startAuthMutation.isPending}
          >
            {startAuthMutation.isPending ? '認証画面を開いています…' : 'Google アカウントで連携 (自動検出)'}
          </Button>

          <details style={{ marginTop: 12 }}>
            <summary style={{ cursor: 'pointer', fontSize: 13, color: 'rgba(245, 241, 230, 0.7)' }}>
              特定の customer_id を指定して連携 (詳細設定)
            </summary>
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="customer-id">customer_id (ハイフン無しの 10 桁数字)</label>
                <input
                  id="customer-id"
                  type="text"
                  className="form-input"
                  value={customerIdDraft}
                  onChange={(e) => setCustomerIdDraft(e.target.value)}
                  placeholder="例: 1234567890"
                  inputMode="numeric"
                />
              </div>
              <Button
                onClick={handleSpecificCustomerConnect}
                variant="muted"
                size="md"
                disabled={startAuthMutation.isPending || !customerIdDraft.trim()}
              >
                指定 customer_id のみ連携
              </Button>
            </div>
          </details>

          {actionError && <p className="error-message">{actionError}</p>}
        </div>
      </NewConnectionModal>
      {dialog}
    </div>
  );
}

